import { ApolloError } from '@apollo/client'
import type { FC } from 'react'
import { useState, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import documentRequestConfigs, {
  buildPromptRefereeMessage,
} from '@/components/DocumentRequests/utils/documentRequestConfigs'
import type {
  DocumentRequestConfig,
  RefereeDetails,
} from '@/components/DocumentRequests/utils/types'
import H6 from '@/components/H6'
import TextAreaInput from '@/components/input/TextAreaInput'
import TextInput from '@/components/input/TextInput'
import LoadingSpinner from '@/components/LoadingSpinner'
import type { SelectOption } from '@/components/Select'
import Select from '@/components/Select'
import ctx from '@/context/AppContext'
import { useAddDocumentRequestMutation } from '@/lib/apollo/mutation/add-document-request'
import { useCreateIdentityDataRequestMutation } from '@/lib/apollo/mutation/createIdentityDataRequest'
import type { IdentityRequestType } from '@/lib/apollo/mutation/createIdentityDataRequest/mutation'
import { IDENTITY_RECOMMENDATION_TYPE } from '@/lib/apollo/mutation/createIdentityDataRequest/mutation'
import { DocumentType } from '@/lib/apollo/query/get-document-requests'
import type {
  SectionName,
  GetDocumentRequestsData,
} from '@/lib/apollo/query/get-document-requests'
import { getErrorsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'

export interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  onBack: () => void
  onIdentityChaserSent?: () => void
  sectionName: SectionName
  subjectName: string
  documentRequestData: GetDocumentRequestsData | undefined
  isCorporateTenantSubject: boolean
  refereeDetails?: RefereeDetails
}

const UNEXPECTED_ERROR_MESSAGE = 'An unexpected error occurred.'

const toIdentityChaserErrorMessage = (error: unknown): string => {
  if (error instanceof ApolloError) {
    const description = getErrorsFromGraphQLExtensions(error).find(
      upstreamError => upstreamError.description,
    )?.description

    return description ?? UNEXPECTED_ERROR_MESSAGE
  }

  return UNEXPECTED_ERROR_MESSAGE
}

const DocumentAlreadyRequestedWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  font-size: 24px;
  text-align: center;
  margin-top: 20px;
  margin-bottom: 20px;
  color: red;
  font-weight: bold;
`

const Container = styled.div`
  display: grid;
  gap: 30px;
`

const FormItemContainer = styled.div`
  margin-top: 10px;
`

const StyledTextAreaInput = styled(TextAreaInput)`
  height: 220px;
`

const RequestMessageBodyContainer = styled.p`
  white-space: pre-wrap;
  word-break: break-all;
`

const ConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-size: 16px;
`

const toEmailSubject = (subject: string) => `Goodlord | ${subject}`

const toEmailBody = (requestMessage: string) => `Thank you for your application.

${requestMessage}`

const toAgentComment = (
  requestMessage: string,
): string => `Goodlord Referencing Team have reviewed the application and have sent the following request to the applicant:

${requestMessage}`

const toInternalCommentHeading = (sectionName: SectionName) =>
  `Additional ${sectionName} document requested`

const documentTypeLabelOverrides: Partial<Record<DocumentType, string>> = {
  [DocumentType.other]: 'Outstanding ID document required',
  [DocumentType.shareCode]: 'Right to Rent Information Required',
  [DocumentType.thirdPartyConfirmation]: '(Other) Third party paying rental',
  [DocumentType.proofOfSubmission]: '(Self employed) Proof of Submission Only',
}

const toDocumentTypeLabel = (documentType: DocumentType): string =>
  documentTypeLabelOverrides[documentType] ?? documentType

const CreateDocumentRequest: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  onBack,
  onIdentityChaserSent,
  sectionName,
  subjectName,
  documentRequestData,
  isCorporateTenantSubject,
  refereeDetails,
}) => {
  const { personId } = useContext(ctx)

  const availableDocumentRequestConfigs = documentRequestConfigs.filter(
    documentRequestConfig =>
      documentRequestConfig.sectionName === sectionName &&
      documentRequestConfig.corporateLetTenant === isCorporateTenantSubject,
  )

  const documentTypeSelectOptions: SelectOption<DocumentType>[] =
    availableDocumentRequestConfigs
      .sort((a, b) =>
        a.documentType.toLowerCase() > b.documentType.toLowerCase() ? 1 : -1,
      )
      .map(({ documentType }) => ({
        label: toDocumentTypeLabel(documentType),
        value: documentType,
      }))

  const getDefaultRequestMessage = (
    documentRequestConfig: DocumentRequestConfig,
  ): string =>
    documentRequestConfig.documentType === DocumentType.promptReferee
      ? buildPromptRefereeMessage(refereeDetails)
      : documentRequestConfig.defaultRequestMessage

  const [selectedDocumentRequestConfig, setSelectedDocumentRequestConfig] =
    useState<DocumentRequestConfig>(availableDocumentRequestConfigs[0])

  const [requestMessage, setRequestMessage] = useState<string>(
    getDefaultRequestMessage(availableDocumentRequestConfigs[0]),
  )

  const [internalComment, setInternalComment] = useState<string>(
    availableDocumentRequestConfigs[0].defaultInternalComment,
  )

  const [emailSubject, setEmailSubject] = useState<string>(
    toEmailSubject(selectedDocumentRequestConfig.emailSubject),
  )

  const [performAddDocumentRequest, { loading, error, data }] =
    useAddDocumentRequestMutation()

  const [performCreateDataRequest] = useCreateIdentityDataRequestMutation()
  const identityChaserInFlight = useRef(false)
  const [isSendingIdentityChaser, setIsSendingIdentityChaser] = useState(false)
  const [identityChaserErrorMessage, setIdentityChaserErrorMessage] = useState<string>()

  const emailBody = toEmailBody(requestMessage)
  const agentComment = toAgentComment(requestMessage)
  const [confirmationStepValidationCopy, setConfirmationStepValidationCopy] =
    useState<string>('')

  const [currentView, setCurrentView] = useState<
    'request-document' | 'confirm-document-request'
  >('request-document')

  const sendIdentityChaser = async (requestType: IdentityRequestType) => {
    if (identityChaserInFlight.current) {
      return
    }

    identityChaserInFlight.current = true
    setIsSendingIdentityChaser(true)
    setIdentityChaserErrorMessage(undefined)

    try {
      await performCreateDataRequest({
        variables: {
          customerId,
          subjectId,
          personId: personId ?? '',
          requestSubject: emailSubject,
          requestMessage: emailBody,
          recommendationType: IDENTITY_RECOMMENDATION_TYPE,
          requestType,
          documentSection: sectionName,
          agentComment,
          internalComment,
        },
      })
    } catch (chaserError) {
      identityChaserInFlight.current = false
      setIsSendingIdentityChaser(false)
      setIdentityChaserErrorMessage(toIdentityChaserErrorMessage(chaserError))
      return
    }

    identityChaserInFlight.current = false
    ;(onIdentityChaserSent ?? onBack)()
  }

  useEffect(() => {
    const activeDocumentRequests =
      documentRequestData?.getDocumentRequests.filter(({ completed }) => !completed) ?? []

    const completedDocumentRequests =
      documentRequestData?.getDocumentRequests.filter(({ completed }) => completed) ?? []

    const hasPendingSelectedDocumentRequest = activeDocumentRequests.find(
      ({ documentType }) => documentType === selectedDocumentRequestConfig.documentType,
    )

    const hasCompletedSelectedDocumentRequest = completedDocumentRequests.find(
      ({ documentType }) => documentType === selectedDocumentRequestConfig.documentType,
    )

    if (activeDocumentRequests.length > 0 && hasPendingSelectedDocumentRequest) {
      setConfirmationStepValidationCopy(
        'This document request type is already open and awaiting response',
      )
    } else if (
      completedDocumentRequests.length > 0 &&
      hasCompletedSelectedDocumentRequest
    ) {
      setConfirmationStepValidationCopy(
        'This document request type has previously been requested for this applicant',
      )
    } else {
      setConfirmationStepValidationCopy('')
    }
  }, [selectedDocumentRequestConfig, documentRequestData])

  return loading ? (
    <LoadingSpinner size="2x" />
  ) : (
    <Container>
      {currentView === 'request-document' && (
        <>
          <div>
            <H6>What type of document are you requesting?</H6>
            <FormItemContainer>
              <Select
                name="document-type"
                disabled={isSendingIdentityChaser}
                selectedValue={selectedDocumentRequestConfig.documentType}
                options={documentTypeSelectOptions}
                onSelect={documentType => {
                  const documentRequestConfig = documentRequestConfigs.find(
                    ({ documentType: dt }) => dt === documentType,
                  ) as DocumentRequestConfig

                  setSelectedDocumentRequestConfig(documentRequestConfig)
                  setRequestMessage(getDefaultRequestMessage(documentRequestConfig))
                  setInternalComment(documentRequestConfig.defaultInternalComment)
                  setEmailSubject(toEmailSubject(documentRequestConfig.emailSubject))
                  setIdentityChaserErrorMessage(undefined)
                }}
              />
            </FormItemContainer>
          </div>
          <div>
            <H6>Request message</H6>
            <StyledTextAreaInput
              value={requestMessage}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setRequestMessage(e.target.value)
              }
            />
          </div>
          <div>
            <H6>Email subject</H6>
            {selectedDocumentRequestConfig.documentType === DocumentType.freeText ? (
              <TextInput
                name="email-subject"
                type="text"
                value={emailSubject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmailSubject(e.target.value)
                }
                label="Email subject"
              />
            ) : (
              <p>{emailSubject}</p>
            )}
          </div>
          <div>
            <H6>Email body</H6>
            <div>
              <p>Hi {subjectName}</p>
            </div>
            <RequestMessageBodyContainer>{emailBody}</RequestMessageBodyContainer>
            <div>
              <p>Regards</p>
              <p>Team Goodlord</p>
            </div>
          </div>
          <div>
            <H6>Agent comment</H6>
            <RequestMessageBodyContainer>{agentComment}</RequestMessageBodyContainer>
          </div>
          <div>
            <H6>Internal comment</H6>
            <p>{toInternalCommentHeading(sectionName)}</p>
            <FormItemContainer>
              <StyledTextAreaInput
                value={internalComment}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setInternalComment(e.target.value)
                }
              />
            </FormItemContainer>
          </div>
        </>
      )}

      {currentView === 'confirm-document-request' && (
        <ConfirmationWrapper>
          <H6>{toDocumentTypeLabel(selectedDocumentRequestConfig.documentType)}</H6>

          <DocumentAlreadyRequestedWrapper>
            <img src="/assets/siren.png" alt="" />
            {confirmationStepValidationCopy}
            <img src="/assets/siren.png" alt="" />
          </DocumentAlreadyRequestedWrapper>

          <div>Are you sure you wish to proceed?</div>
        </ConfirmationWrapper>
      )}

      {error && <>An unexpected error occurred. {error?.message}</>}
      {identityChaserErrorMessage && <div role="alert">{identityChaserErrorMessage}</div>}
      <div className="side-by-side-flex-end">
        <AlternateButton
          disabled={isSendingIdentityChaser}
          onClick={() =>
            currentView === 'request-document'
              ? onBack()
              : setCurrentView('request-document')
          }
        >
          Back
        </AlternateButton>
        {!data && (
          <Button
            disabled={
              loading ||
              isSendingIdentityChaser ||
              requestMessage === '' ||
              emailSubject === ''
            }
            onClick={() => {
              if (
                currentView === 'request-document' &&
                confirmationStepValidationCopy !== ''
              ) {
                setCurrentView('confirm-document-request')
              } else if (selectedDocumentRequestConfig.identityRequestType) {
                sendIdentityChaser(selectedDocumentRequestConfig.identityRequestType)
              } else {
                performAddDocumentRequest({
                  variables: {
                    customerId,
                    applicationId,
                    subjectId,
                    personId: personId ?? '',
                    emailSubject,
                    requestMessage: emailBody,
                    documentType: selectedDocumentRequestConfig.documentType,
                    documentSection: sectionName,
                    agentComment,
                    internalComment,
                  },
                })
              }
            }}
          >
            {confirmationStepValidationCopy === '' ? 'Confirm' : 'Continue'}
          </Button>
        )}
      </div>
    </Container>
  )
}

export default CreateDocumentRequest
